import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  LIST_VIEW_ACTIONS,
  ListType,
  ListViewMode,
  selectSelectedListViewModel,
  selectSelectedListViewMode,
  IListViewState,
  selectSelectedListItemKey,
  selectSelectedListId,
  selectSelectedListType,
} from '@usf/ngrx-list';
import { EASY_PRICING_ACTIONS } from '@usf/ngrx-pricing';
import { filter, map, switchMap, withLatestFrom, tap } from 'rxjs';
import { MenuController, ModalController } from '@ionic/angular';
import { PlatformService } from '@panamax/app-state';

@Injectable()
export class ClientListViewEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<IListViewState>,
    private readonly platformService: PlatformService,
    private readonly messageStoreService: MessageStoreService,
    private readonly translateService: TranslateService,
    private readonly menuController: MenuController,
  ) {}

  loadPmeForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LIST_VIEW_ACTIONS.selectList,
        LIST_VIEW_ACTIONS.changeListViewMode,
      ),
      withLatestFrom(this.store.select(selectSelectedListViewModel)),
      filter(
        ([action, selectedListViewModel]) =>
          selectedListViewModel.selectedListViewMode === ListViewMode.pricing &&
          selectedListViewModel.selectedListType !== null,
      ),
      map(([action, selectedListViewModel]) => {
        // Create the base action payload
        const payload: any = {
          productNumbers:
            selectedListViewModel.separatedProductNumbers.usfProductNumbers,
          fridayPricing: selectedListViewModel.fridayToggleOn,
          overrideCache: action?.overrideCache ?? false,
        };

        // Add feature parameter based on list type
        if (
          selectedListViewModel.selectedListType === ListType.orderGuide &&
          selectedListViewModel.selectedListId
        ) {
          // Order Guide format
          payload.feature = `Order Guide - ${selectedListViewModel.selectedListId}`;
        } else if (
          selectedListViewModel.selectedListType === ListType.recentPurchase
        ) {
          // Recently Purchased
          payload.feature = 'Recently Purchased';
        }

        return EASY_PRICING_ACTIONS.loadEasyPricing(payload);
      }),
    ),
  );

  handleEasyPricingFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EASY_PRICING_ACTIONS.fetchEasyPricingFail),
      withLatestFrom(this.store.select(selectSelectedListViewModel)),
      filter(
        ([action, selectedListViewModel]) =>
          selectedListViewModel.selectedListViewMode === ListViewMode.pricing &&
          action.isFromFridayToggle,
      ),
      tap(([action, selectedListProductNumbers]) =>
        this.messageStoreService.upsertMessage({
          type: MessageTypeEnum.error,
          display: this.translateService.instant(
            selectedListProductNumbers.fridayToggleOn
              ? 'i18n.lists.pmeList.nextWeekPricingLoadFail'
              : 'i18n.lists.pmeList.currentWeekPricingLoadFail',
          ),
          toast: true,
          sticky: false,
        } as Message),
      ),
      map(() => LIST_VIEW_ACTIONS.setFridayToggle({})), // Reset toggle if an error occurs to what it was before
    ),
  );

  // Effect to handle menu opening/closing based on selectedListItemKey changes
  handlePmeMenuVisibility$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_VIEW_ACTIONS.setSelectedListItemKey),
        filter(
          () =>
            this.platformService.platformType ===
            this.platformService.platformEnum.mobile,
        ),
        withLatestFrom(
          this.store.select(selectSelectedListViewMode),
          this.store.select(selectSelectedListItemKey),
          this.store.select(selectSelectedListId),
        ),
        tap(async ([action, viewMode, selectedListItemKey, selectedListId]) => {
          try {
            if (viewMode === ListViewMode.pricing && selectedListItemKey) {
              await this.menuController.open(
                'right-rail-menu-' + selectedListId,
              );
            } else {
              const isOpen = await this.menuController.isOpen(
                'right-rail-menu-' + selectedListId,
              );
              if (isOpen) {
                await this.menuController.close(
                  'right-rail-menu-' + selectedListId,
                );
              }
            }
          } catch (error) {}
        }),
      ),
    { dispatch: false },
  );
  resetEasyPricingProcessingStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LIST_VIEW_ACTIONS.changeListViewMode),
      filter(action => action.listViewMode === ListViewMode.pricing),
      map(() => EASY_PRICING_ACTIONS.resetProcessingStates()),
    ),
  );
}
